import React, { useState } from "react";
import { Link } from "gatsby";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "../data/navLinks";
import { StaticImage } from "gatsby-plugin-image";

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <>
      {/* =========================================
          MOBILE HEADER (< md)
          Floating Glass "Island" Top Bar
      ========================================= */}
      <header className="fixed top-4 left-4 right-4 z-50 lg:hidden glass rounded-2xl flex items-center justify-between px-4 h-16 transition-all duration-300">
        {/* Logo Area */}
        <Link to="/" className="flex items-center gap-2">
           <StaticImage src="../images/logo.png" alt="Logo" className="w-14 h-14 rounded-lg"/>
           <span className="text-brand-dark font-bold tracking-wider">KHALIFA NIYAS</span>
        </Link>

        {/* Hamburger Toggle */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="text-brand-dark p-2 hover:bg-brand-secondary/20 rounded-xl transition-colors"
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        {/* Mobile Menu Dropdown (Glass Overlay) */}
        <div
          className={`absolute top-[calc(100%+0.5rem)] left-0 right-0 rounded-2xl overflow-hidden transition-all duration-300 origin-top shadow-xl ${
            isMobileMenuOpen 
              ? "max-h-[80vh] opacity-100 scale-y-100 bg-brand-dark border border-white/10" 
              : "max-h-0 opacity-0 scale-y-95 glass"
          }`}
        >
          <nav className="flex-col p-2 space-y-1 flex">
            {NAV_LINKS.map((link, index) => {
              const kIsAi = link.label === "TeamICT AI";
              const isExternal = link.href.startsWith("http");
              const Icon = link.icon;
              
              const LinkComponent = isExternal ? "a" : Link;
              const linkProps = isExternal ? { href: link.href, target: "_blank", rel: "noopener noreferrer" } : { to: link.href };

              return (
                // @ts-ignore
                <LinkComponent
                  key={index}
                  {...linkProps}
                  className={`flex items-center gap-4 p-3 rounded-xl transition-all group ${
                    kIsAi 
                      ? "bg-gradient-to-r from-cyan-500/20 to-blue-600/20 text-cyan-400 border border-cyan-500/30 shadow-[0_0_15px_rgba(6,182,212,0.15)]" 
                      : "text-gray-300 hover:text-brand-primary hover:bg-white/5"
                  }`}
                  {...(!isExternal ? {
                    activeClassName: "!text-brand-primary bg-white/10 font-semibold shadow-sm"
                  } : {})}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <Icon 
                    size={20} 
                    className={`transition-colors ${kIsAi ? "text-cyan-300 drop-shadow-[0_0_8px_rgba(34,211,238,0.8)]" : "group-hover:text-brand-primary"}`} 
                  />
                  <span className={`font-medium tracking-wide ${kIsAi ? "font-bold" : ""}`}>{link.label}</span>
                </LinkComponent>
              );
            })}
          </nav>
        </div>
      </header>

      {/* =========================================
          DESKTOP SIDEBAR (>= md)
          Floating Glass "Island" Sidebar
      ========================================= */}
      <aside className="hidden lg:flex flex-col fixed left-4 top-1/2 -translate-y-1/2 z-50 glass rounded-2xl w-20 hover:w-64 transition-all duration-500 ease-out group overflow-hidden max-h-[90vh] shadow-2xl border border-white/10">
        
        {/* Top: Logo Area */}
        <div className="h-24 flex items-center justify-center w-20 shrink-0 border-b border-white/20">
          <Link to="/" className="flex items-center justify-center w-full h-full relative">
             <StaticImage src="../images/logo.png" alt="Logo" className="w-14 h-14 rounded-lg" />
             <div className="absolute left-20 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-brand-dark font-bold tracking-widest text-lg">
               KHALIFA NIYAS
             </div>
          </Link>
        </div>

        {/* Middle: Navigation Links */}
        <nav className="flex-1 py-6 flex flex-col gap-2 overflow-y-auto overflow-x-hidden scrollbar-hide px-2">
          {NAV_LINKS.map((link, index) => {
            const kIsAi = link.label === "TeamICT AI";
            const isExternal = link.href.startsWith("http");
            const Icon = link.icon;

            const LinkComponent = isExternal ? "a" : Link;
            const linkProps = isExternal ? { href: link.href, target: "_blank", rel: "noopener noreferrer" } : { to: link.href };

            return (
              // @ts-ignore
              <LinkComponent
                key={index}
                {...linkProps}
                className={`relative flex items-center h-12 px-0 rounded-xl transition-all duration-300 ${
                  kIsAi
                    ? "text-cyan-400 bg-cyan-500/10 hover:bg-cyan-500/20 border-l-2 border-cyan-400"
                    : "text-brand-dark/70 hover:text-brand-primary hover:bg-white/50"
                }`}
                {...(!isExternal ? {
                  activeClassName: "!text-brand-primary bg-white/80 shadow-sm"
                } : {})}
              >
                {/* Icon Container */}
                <div className="w-16 shrink-0 flex items-center justify-center">
                  <Icon 
                    size={kIsAi ? 26 : 24} 
                    strokeWidth={kIsAi ? 2 : 1.5} 
                    className={kIsAi ? "drop-shadow-[0_0_5px_rgba(34,211,238,0.5)] animate-pulse" : ""}
                  />
                </div>

                {/* Text Label - revealed on hover */}
                <span className={`whitespace-nowrap font-medium text-sm tracking-wider opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform translate-x-4 group-hover:translate-x-0 ${kIsAi ? "text-cyan-400 font-bold" : ""}`}>
                  {link.label}
                </span>
              </LinkComponent>
            );
          })}
        </nav>

        {/* Bottom: User/Settings or decorative tech element */}
        <div className="p-4 border-t border-white/20 bg-white/10">
          <div className="flex items-center text-xs text-brand-dark/50 font-mono overflow-hidden whitespace-nowrap">
             <div className="w-12 shrink-0 flex items-center justify-center">
               <div className="w-2 h-2 rounded-full bg-brand-primary animate-pulse shadow-[0_0_8px_rgba(92,131,116,0.6)]"></div>
             </div>
             <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
               SYSTEM ONLINE
             </span>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Header;
